import { Typography } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect } from 'react'
import FeatureToggleRow from './components/FeatureToggleRow'
import { useAccountFeatures, ACCOUNT_FEATURES } from '../../hooks/account-features'
import lodashGet from 'lodash.get'
import CanvasSsoFeatureToggle from "./components/CanvasSsoFeatureToggle";
import HelloStackFeatureToggle from "./components/HelloStackFeatureToggle";

const ENV = process.env.ENV

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2),
    },
}))

const CustomerFeatureToggle = ({ id }) => {
    const classes = useStyles()

    const featureController = useAccountFeatures({ accountId: id })

    useEffect(() => {
        featureController.retrieveFeatures()
    }, [id])

    const isFeatEnabled = (feat) => lodashGet(featureController.features, `${feat}.enabled`, false)
    const getFeatErrorMsg = (feat) => lodashGet(featureController.updateErrors, `${feat}.enabled.msg`)

    return (
        <>
            <label className="box-label">Toggle Features</label>
            <Paper className={classes.container}>
                <Grid container>
                    <Grid item xs={3}>
                        <Typography align="center" variant="subtitle1">
                            Feature
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography align="center" variant="subtitle1">
                            Description
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography align="center" variant="subtitle1">
                            Enabled
                        </Typography>
                    </Grid>
                </Grid>
                <hr />
                    <FeatureToggleRow
                        name="Tempo"
                        description="Provides customers with estimated arrival times of end-users."
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.Tempo)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.Tempo)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.Tempo}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.Tempo)))}
                    />
                    <FeatureToggleRow
                        name="Real-time Dashboard"
                        description="Allows to see list of Zones with the most activity in Canvas"
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.RealTimeDashboard)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.RealTimeDashboard)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.RealTimeDashboard}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.RealTimeDashboard)))}
                    />
                    <FeatureToggleRow
                        name="Hello Screens"
                        description="Allows customers to use Hello Screens product"
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.Screens)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.Screens)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.Screens}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.Screens)))}
                    />
                    <FeatureToggleRow
                        name="Bluedot Insights"
                        description="Allows customers to view Bluedot Insights charts in Canvas."
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.Insights)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.Insights)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.Insights}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.Insights)))}
                    />
                    <FeatureToggleRow
                        name="Hide Stores"
                        description="In Canvas, hides the Stores functionality for customers that it is not suitable for. Defaults to showing Stores"
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.HideStores)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.HideStores)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.HideStores}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.HideStores)))}
                    />
                    <FeatureToggleRow
                        name="Now Ready Configuration"
                        description="Shows the Now Ready section in Canvas, which allows the user to set up Now Ready branding for their projects so that Now Ready can be used"
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.NowReadyConfiguration)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.NowReadyConfiguration)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.NowReadyConfiguration}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.NowReadyConfiguration)))}
                    />
                    <FeatureToggleRow
                        name="Automatic Destination Creation"
                        description="DO NOT ENABLE FOR NEW CUSTOMERS - When creating Zones via the API, automatically create Destinations if a Zone is posted with a destinationId and the Destination does not exist in the project. This feature is deprecated and is only to support backwards compatibility for customers who were previously creating Zones with destinationIds via the API"
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.AutomaticDestinationCreation)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.AutomaticDestinationCreation)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.AutomaticDestinationCreation}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.AutomaticDestinationCreation)))}
                    />
                    <CanvasSsoFeatureToggle
                        name="Canvas SSO"
                        description="Enable Canvas login via the customer’s external identity provider"
                        feat={lodashGet(featureController.features, `${ACCOUNT_FEATURES.CanvasSso}`)}
                        updateCanvasSso={(canvasSso) => featureController.patchFeatures(`${ACCOUNT_FEATURES.CanvasSso}`, canvasSso)}
                        deleteCanvasSso={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.CanvasSso}`, undefined, "remove")}
                        errorMsg={featureController.updateErrors[ACCOUNT_FEATURES.CanvasSso]?.msg}
                    />
                    <FeatureToggleRow
                        name="Hello State Templates"
                        description="Enables setting Hello State Template values via the Stores page in Canvas. Allows Store level state overrides and setting project level state configurations via Canvas"
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.HelloStateTemplates)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.HelloStateTemplates)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.HelloStateTemplates}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.HelloStateTemplates)))}
                    />
                    <HelloStackFeatureToggle
                        name="Hello Stack"
                        description="Select the Hello Stack for your account. Default to None to use the Core stack"
                        feat={lodashGet(featureController.features, `${ACCOUNT_FEATURES.HelloStack}`)}
                        updateHelloStack={(helloStack) => featureController.patchFeatures(`${ACCOUNT_FEATURES.HelloStack}`, helloStack)}
                        errorMsg={featureController.updateErrors[ACCOUNT_FEATURES.HelloStack]?.msg}
                    />
                    <FeatureToggleRow
                        name="Push Notifications"
                        description="Enables push notification campaign capabilities for the account"
                        loading={featureController.loading}
                        errorMsg={getFeatErrorMsg(ACCOUNT_FEATURES.PushNotifications)}
                        value={isFeatEnabled(ACCOUNT_FEATURES.PushNotifications)}
                        setValue={() => featureController.patchFeatures(`${ACCOUNT_FEATURES.PushNotifications}.enabled`, !(isFeatEnabled(ACCOUNT_FEATURES.PushNotifications)))}
                    />
            </Paper>
        </>
    )
}

export default CustomerFeatureToggle
